import React, { useState, useEffect, useRef } from 'react';
import IncidentRow from './IncidentRow.jsx';

const ZONE_ALL = '__all__';
const DATE_OPTIONS = [
  { label: '3 days',  days: 3  },
  { label: '7 days',  days: 7  },
  { label: '15 days', days: 15 },
  { label: '30 days', days: 30 },
];

const TYPE_GROUPS = [
  { value: '__all__',  label: 'All types' },
  { value: 'attack',   label: 'Attacks'  },
  { value: 'approach', label: 'Approaches / Sightings' },
  { value: 'piracy',   label: 'Piracy'   },
  { value: 'other',    label: 'Other'    },
];

const ATTACK_TYPES   = ['drone_attack','missile_attack','hijack_attempt','vessel_seizure','armed_robbery'];
const APPROACH_TYPES = ['suspicious_approach','piracy_approach','sighting','missile_sighting','vessel_harassment'];
const PIRACY_TYPES   = ['piracy','piracy_approach','armed_robbery'];

function typeGroup(type) {
  if (ATTACK_TYPES.includes(type))   return 'attack';
  if (APPROACH_TYPES.includes(type)) return 'approach';
  if (PIRACY_TYPES.includes(type))   return 'piracy';
  return 'other';
}

const chipStyle = (active) => ({
  padding: '5px 12px',
  borderRadius: 6,
  border: active ? '1px solid #e9a020' : '1px solid #1e2d44',
  background: active ? 'rgba(233,160,32,0.08)' : 'transparent',
  color: active ? '#e9a020' : '#8892a5',
  fontSize: 12, fontFamily: "'Figtree', sans-serif", fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export default function IncidentsAccordion({ incidents, zones, defaultZoneId, onClear }) {
  const [open,        setOpen]       = useState(true);
  const [days,        setDays]       = useState(3);
  const [zoneFilter,  setZoneFilter] = useState(ZONE_ALL);
  const [typeFilter,  setTypeFilter] = useState('__all__');
  const ref = useRef(null);

  // When a ZoneCard says "View incidents", open + filter
  useEffect(() => {
    if (defaultZoneId) {
      setOpen(true);
      setZoneFilter(defaultZoneId);
      setDays(7);
    }
  }, [defaultZoneId]);

  // Scroll into view when opened from ZoneCard
  useEffect(() => {
    if (open && defaultZoneId && ref.current) {
      setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [open, defaultZoneId]);

  const cutoff  = Date.now() - days * 24 * 60 * 60 * 1000;
  const filtered = (incidents || []).filter(inc => {
    if (new Date(inc.date).getTime() < cutoff) return false;
    if (zoneFilter !== ZONE_ALL && inc.zone_id !== zoneFilter) return false;
    if (typeFilter !== '__all__' && typeGroup(inc.type) !== typeFilter) return false;
    return true;
  });

  const totalRecent7 = (incidents || []).filter(
    i => new Date(i.date).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000
  ).length;

  // Unique zones in incidents for dropdown
  const zoneOptions = [{ id: ZONE_ALL, name: 'All zones' }];
  const seenZones = new Set();
  (incidents || []).forEach(i => {
    if (!seenZones.has(i.zone_id)) {
      seenZones.add(i.zone_id);
      zoneOptions.push({ id: i.zone_id, name: i.zone });
    }
  });

  return (
    <div ref={ref} style={{ background: '#111a2e', borderRadius: 10, border: '1px solid #1e2d44', overflow: 'hidden' }}>
      {/* Header — always visible */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
          padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}
      >
        <div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 600, color: '#fff' }}>
            Recent Incidents (last 7 days) — {totalRecent7} total
          </div>
          <div style={{ fontSize: 11, color: '#8892a5', marginTop: 2 }}>
            Live incident feed · click to expand
          </div>
        </div>
        <span style={{ color: '#8892a5', fontSize: 14, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <>
          {/* Filters */}
          <div style={{
            padding: '12px 16px', borderTop: '1px solid #1e2d44', borderBottom: '1px solid #1e2d44',
            background: '#0d1525', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            {/* Zone dropdown */}
            <select
              value={zoneFilter}
              onChange={e => setZoneFilter(e.target.value)}
              style={{
                background: '#111a2e', border: '1px solid #1e2d44', color: '#e2e8f0',
                borderRadius: 6, padding: '5px 10px', fontSize: 11,
                fontFamily: "'Figtree', sans-serif", cursor: 'pointer',
              }}
            >
              {zoneOptions.map(z => (
                <option key={z.id} value={z.id}>{z.name}</option>
              ))}
            </select>

            {/* Date range chips */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DATE_OPTIONS.map(opt => (
                <button key={opt.days} onClick={() => setDays(opt.days)} style={chipStyle(days === opt.days)}>
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Type filter chips */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {TYPE_GROUPS.map(g => (
                <button key={g.value} onClick={() => setTypeFilter(g.value)} style={chipStyle(typeFilter === g.value)}>
                  {g.label}
                </button>
              ))}
            </div>

            {/* Clear zone filter if active */}
            {(zoneFilter !== ZONE_ALL || typeFilter !== '__all__') && (
              <button
                onClick={() => { setZoneFilter(ZONE_ALL); setTypeFilter('__all__'); onClear?.(); }}
                style={{ ...chipStyle(false), color: '#E11D48', borderColor: 'rgba(225,29,72,0.3)' }}
              >
                Clear filters ✕
              </button>
            )}
          </div>

          {/* Incident list */}
          <div>
            {filtered.length === 0 ? (
              <div style={{ padding: '24px 20px', color: '#8892a5', fontSize: 13 }}>
                No incidents match the selected filters. Try expanding the date range.
              </div>
            ) : (
              filtered.map(inc => <IncidentRow key={inc.id} incident={inc} />)
            )}
          </div>

          {/* Date range expand hint */}
          {days < 30 && (
            <div style={{
              padding: '12px 20px', borderTop: '1px solid #1e2d44',
              background: '#0d1525', textAlign: 'center',
            }}>
              <button
                onClick={() => setDays(d => Math.min(d === 3 ? 7 : d === 7 ? 15 : 30, 30))}
                style={{
                  background: 'none', border: '1px solid #1e2d44', color: '#8892a5',
                  borderRadius: 6, padding: '6px 16px',
                  fontSize: 12, fontFamily: "'Figtree', sans-serif", cursor: 'pointer',
                }}
              >
                Load older incidents ({days === 3 ? '7 days' : days === 7 ? '15 days' : '30 days'}) →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
